import { useState } from "react";

import { Card, Icon } from "@blueprintjs/core";

import { Box, Flex } from "@/components/layout/flexbox";
import { CTA_COLOR, LIGHT_BACKGROUND, MIDTEXT_COLOR } from "@/css/constants";

interface CalculatorResultsProps {
  ote: number;
  commissionRate: number;
  annualQuota: number;
  guaranteeMonths1to3: number;
  guaranteeMonths4to6: number;
  rampMonths1to3: number;
  rampMonths4to6: number;
  rampMonths7to9: number;
  rampMonths10to12: number;
  accelerators: { threshold: number; multiplier: number }[];
}

function CalculatorResults({
  ote,
  commissionRate,
  annualQuota,
  guaranteeMonths1to3,
  guaranteeMonths4to6,
  rampMonths1to3,
  rampMonths4to6,
  rampMonths7to9,
  rampMonths10to12,
  accelerators,
}: CalculatorResultsProps) {
  const [isProtectionSectionOpen, setIsProtectionSectionOpen] = useState(false);
  return (
    <>
      {/* Summary Metrics */}
      <Box>
        <Box
          as="h2"
          mb={3}
          fontSize="18px"
          fontWeight={600}
          color={MIDTEXT_COLOR}
        >
          Comp Overview
        </Box>

        <Flex flexWrap="wrap" css={{ gap: "16px" }}>
          <Card
            style={{
              backgroundColor: LIGHT_BACKGROUND,
              width: "calc(50% - 8px)",
              padding: "16px",
              margin: 0,
            }}
          >
            <Box fontSize="14px" color="#6B7280">
              OTE
            </Box>
            <Box fontSize="20px" fontWeight={700} color="#111827">
              ${Math.round(ote).toLocaleString()}
            </Box>
          </Card>
          <Card
            style={{
              backgroundColor: LIGHT_BACKGROUND,
              width: "calc(50% - 8px)",
              padding: "16px",
              margin: 0,
            }}
          >
            <Box fontSize="14px" color="#6B7280">
              Annual Quota (Calculated)
            </Box>
            <Box fontSize="20px" fontWeight={700} color="#111827">
              ${Math.round(annualQuota).toLocaleString()}
            </Box>
          </Card>
        </Flex>
      </Box>

      {/* Protections & Accelerators Info Cards */}
      <Flex mt={3} mb={2} alignItems="center">
        <Flex
          alignItems="center"
          onClick={() => setIsProtectionSectionOpen(!isProtectionSectionOpen)}
          css={{ cursor: "pointer" }}
        >
          <Box fontWeight={600} color={CTA_COLOR} margin={0} mr={2}>
            See protections & accelerators summary
          </Box>
          <Icon
            icon={isProtectionSectionOpen ? "chevron-up" : "chevron-down"}
            color={CTA_COLOR}
          />
        </Flex>
      </Flex>
      {isProtectionSectionOpen && (
        <Box fontSize="14px" color={MIDTEXT_COLOR}>
          {/* Commission Guarantee Card */}
          <Card
            style={{
              backgroundColor: LIGHT_BACKGROUND,
              padding: "12px",
              border: "none",
              boxShadow: "none",
            }}
          >
            <Box fontWeight={500} color="#166534">
              Commission Guarantee
            </Box>
            <Box mt={1} color="#15803D">
              Months 1-3: {guaranteeMonths1to3}% of target commission guaranteed
              <br />
              Months 4-6: {guaranteeMonths4to6}% of target commission guaranteed
            </Box>
          </Card>

          {/* Quota Ramp Card */}
          <Card
            style={{
              backgroundColor: LIGHT_BACKGROUND,
              marginTop: "16px",
              padding: "12px",
              border: "none",
              boxShadow: "none",
            }}
          >
            <Box fontWeight={500} color="#92400E">
              Quota Ramp
            </Box>
            <Box mt={1} color="#B45309">
              To account for onboarding and ramp-up time, quota targets are
              adjusted:
              <br />
              Months 1-3: {rampMonths1to3}% of full quota
              <br />
              Months 4-6: {rampMonths4to6}% of full quota
              <br />
              Months 7-9: {rampMonths7to9}% of full quota
              <br />
              Months 10-12: {rampMonths10to12}% of full quota
            </Box>
          </Card>

          {/* Accelerator Tiers Card */}
          <Card
            style={{
              backgroundColor: LIGHT_BACKGROUND,
              marginTop: "16px",
              padding: "12px",
              border: "none",
              boxShadow: "none",
            }}
          >
            <Box fontWeight={500} color="#1E40AF">
              Accelerator Tiers:
            </Box>
            <Box mt={1} color="#2563EB">
              <ul className="list-disc pl-6" style={{ paddingLeft: 16 }}>
                {/* Handle case with no accelerators */}
                {accelerators.length === 0 ? (
                  <li>
                    All performance: {commissionRate}% commission rate (no
                    accelerators)
                  </li>
                ) : (
                  <>
                    {/* First tier is always the base commission rate */}
                    <li>
                      Up to {accelerators[0].threshold}% of quota:{" "}
                      {commissionRate}% commission rate
                    </li>

                    {/* Render dynamic accelerator tiers */}
                    {accelerators.map((accelerator, index) => {
                      if (index === accelerators.length - 1) {
                        return (
                          <li key={index}>
                            Above {accelerator.threshold}% of quota:{" "}
                            {(commissionRate * accelerator.multiplier).toFixed(
                              1,
                            )}
                            % commission rate ({accelerator.multiplier}x
                            multiplier)
                          </li>
                        );
                      } else {
                        return (
                          <li key={index}>
                            {accelerator.threshold}% to{" "}
                            {accelerators[index + 1].threshold}% of quota:{" "}
                            {(commissionRate * accelerator.multiplier).toFixed(
                              1,
                            )}
                            % commission rate ({accelerator.multiplier}x
                            multiplier)
                          </li>
                        );
                      }
                    })}
                  </>
                )}
              </ul>
            </Box>
          </Card>
        </Box>
      )}
    </>
  );
}

export default CalculatorResults;
